// Staff invitations, as the invited person sees them (Sahil, 2026-10-20).
//
//   * useMyInvitations: the one data source -- my_invitations() -- for the
//     banner, the bell and the Company Selector's Pending Invites.
//   * InvitationBanner: "<Company> invited you as <Role> — Accept / Decline",
//     at the top of whichever company they have open.
//   * InviteLinkModal: the emailed /invite/<token> link, after sign-in. The
//     database answers whether the signed-in email is the invited one.
//   * answerInvitation: accept / decline through the database functions,
//     which check the signed-in email, the status, the expiry and the token.
import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabase";
import { Btn } from "../ui";
import { pmError } from "../utils/errors";
import { clearMembershipCache } from "../utils/company";
import { shortDate, pendingInviteToken, clearInviteToken, inviteErrorText } from "../utils/invitations";

export const INVITE_ROLE_LABELS = { admin: "Admin", owner: "Owner", pm: "Property Manager", manager: "Manager", office_assistant: "Office Assistant", accountant: "Accountant", maintenance: "Maintenance", tenant: "Tenant" };
const roleLabel = (r) => INVITE_ROLE_LABELS[r] || r || "a member";

export function useMyInvitations(currentUser) {
  const [invites, setInvites] = useState([]);
  const email = currentUser?.email || "";
  const reload = useCallback(async () => {
    if (!email) { setInvites([]); return []; }
    const { data, error } = await supabase.rpc("my_invitations");
    if (error) { pmError("PM-8001", { raw: error, context: "load my invitations", silent: true }); return []; }
    setInvites(data || []);
    return data || [];
  }, [email]);
  useEffect(() => { reload(); }, [reload]);
  return { invites, open: invites.filter(i => !i.expired), reload };
}

// Accept or decline. Returns { ok, company, role } or { ok: false, error }.
export async function answerInvitation(kind, { companyId = null, token = null } = {}) {
  const fn = kind === "accept" ? "accept_company_invitation" : "decline_company_invitation";
  const { data, error } = await supabase.rpc(fn, { p_company_id: companyId, p_token: token });
  if (error) return { ok: false, error: inviteErrorText(error) };
  clearMembershipCache();
  // The audit row is written by the database function (the invitee cannot
  // write one for a company they are not a member of yet).
  const cid = data?.company_id || companyId;
  let company = null;
  if (kind === "accept" && cid) {
    const { data: co } = await supabase.from("companies").select("*").eq("id", cid).maybeSingle();
    company = co || null;
  }
  return { ok: true, company, role: data?.role || null, companyId: cid };
}

export function InvitationBanner({ invites, busy, onAnswer }) {
  if (!invites?.length) return null;
  return (
    <div className="space-y-2 mb-4" data-testid="invitation-banner">
      {invites.map(i => (
        <div key={i.member_id} className="bg-info-50 border border-info-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-3">
          <span className="material-icons-outlined text-info-600">mail</span>
          <div className="flex-1 min-w-0 text-sm text-info-900">
            <strong>{i.company_name}</strong> invited you as <strong>{roleLabel(i.role)}</strong>
            <span className="text-xs text-info-700"> · {i.inviter_name ? "from " + i.inviter_name + " · " : ""}expires {shortDate(i.expires_at)}</span>
          </div>
          <div className="flex gap-2 shrink-0">
            <Btn size="sm" variant="success-fill" disabled={!!busy} onClick={() => onAnswer("accept", i)}>{busy === "accept:" + i.company_id ? "Joining…" : "Accept"}</Btn>
            <Btn size="sm" variant="secondary" disabled={!!busy} onClick={() => onAnswer("decline", i)}>{busy === "decline:" + i.company_id ? "Declining…" : "Decline"}</Btn>
          </div>
        </div>
      ))}
    </div>
  );
}

const STATE_TEXT = {
  invalid: "This invitation link is not valid. Ask the admin to send a new one.",
  used: "This invitation link has already been used.",
  replaced: "This invitation was replaced by a newer one or cancelled. Use the most recent email, or ask the admin to resend it.",
  expired: "This invitation has expired. Ask the admin to resend it.",
  declined: "You declined this invitation.",
  active: "You already accepted this invitation.",
};

// The emailed link. Renders nothing unless a /invite/<token> was captured
// and someone is signed in (signing in is the link's first step).
export function InviteLinkModal({ currentUser, onAccepted, onDeclined, onLogout, showToast }) {
  const [token, setToken] = useState(() => pendingInviteToken());
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState("");
  const email = currentUser?.email || "";
  useEffect(() => {
    if (!token || !email) return;
    let cancelled = false;
    supabase.rpc("invitation_by_token", { p_token: token }).then(({ data, error }) => {
      if (cancelled) return;
      setInfo(error ? { state: "error", error: inviteErrorText(error) } : (data || { state: "invalid" }));
    });
    return () => { cancelled = true; };
  }, [token, email]);
  if (!token || !email || !info) return null;
  const close = () => { clearInviteToken(); setToken(null); };
  async function answer(kind) {
    setBusy(kind);
    const r = await answerInvitation(kind, { companyId: info.company_id, token });
    setBusy("");
    if (!r.ok) { showToast?.(r.error, "error"); return; }
    close();
    if (kind === "accept") { showToast?.("You joined " + (info.company_name || "the company") + ".", "success"); onAccepted?.(r.company, r.role); }
    else { showToast?.("You declined the invitation to " + (info.company_name || "the company") + ".", "info"); onDeclined?.(); }
  }
  const open = info.state === "open";
  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/30 p-4" role="dialog" aria-label="Invitation">
      <div className="bg-white rounded-xl shadow-pop w-full max-w-md p-6">
        <div className="flex items-center gap-2 mb-3">
          <span className="material-icons-outlined text-brand-600">mail</span>
          <h2 className="text-lg font-bold text-neutral-800">{info.company_name ? "Join " + info.company_name : "Invitation"}</h2>
        </div>
        {open && (
          <p className="text-sm text-neutral-600 mb-4">
            <strong>{info.inviter_name || "An admin"}</strong> invited you to join <strong>{info.company_name}</strong> on Housify as <strong>{roleLabel(info.role)}</strong>. It expires {shortDate(info.expires_at)}.
          </p>
        )}
        {info.state === "wrong_email" && (
          <p className="text-sm text-neutral-600 mb-4">This invitation was sent to <strong>{info.email_hint}</strong>, but you are signed in as <strong>{email}</strong>. Sign out and sign in as that address to answer it.</p>
        )}
        {info.state === "error" && <p className="text-sm text-danger-700 mb-4">{info.error}</p>}
        {STATE_TEXT[info.state] && <p className="text-sm text-neutral-600 mb-4">{STATE_TEXT[info.state]}</p>}
        <div className="flex flex-wrap gap-2 justify-end">
          {open && <Btn size="sm" variant="success-fill" disabled={!!busy} onClick={() => answer("accept")}>{busy === "accept" ? "Joining…" : "Accept"}</Btn>}
          {open && <Btn size="sm" variant="secondary" disabled={!!busy} onClick={() => answer("decline")}>{busy === "decline" ? "Declining…" : "Decline"}</Btn>}
          {info.state === "wrong_email" && onLogout && <Btn size="sm" variant="secondary" onClick={() => { onLogout(); }}>Sign out</Btn>}
          <Btn size="sm" variant="ghost" onClick={close}>{open ? "Decide later" : "Close"}</Btn>
        </div>
      </div>
    </div>
  );
}
